"use client";

import { useState, useEffect, useRef } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useLang } from "@/lib/i18n/LanguageContext";
import { LANGUAGES } from "@/lib/i18n/translations";
import { usePageTransition } from "@/components/layout/PageTransition/PageTransition";
import Icon from "@/components/ui/Icon/Icon";
import { withBasePath } from "@/lib/basePath";

interface NavProps {
  logoVersion?: string;
}

export default function Nav({ logoVersion = "1" }: NavProps) {
  const [scrolled, setScrolled] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const { lang, setLang, t } = useLang();
  const langRef = useRef<HTMLDivElement>(null);
  const pathname = usePathname();
  const { phase, navigate } = usePageTransition();
  const phaseCls =
    phase === "exiting"
      ? " is-exiting"
      : phase === "entering"
        ? " is-entering"
        : "";

  const navLinks = [
    { key: "nav.home", href: "/" },
    { key: "nav.about", href: "/about" },
    { key: "nav.farmers", href: "/farmers" },
    { key: "nav.buyers", href: "/buyers" },
    { key: "nav.logistics", href: "/logistics" },
    { key: "nav.academy", href: "/academy" },
    { key: "nav.contacts", href: "/contacts" },
  ];

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Закрытие dropdown при клике вне
  useEffect(() => {
    if (!langOpen) return;
    const handler = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [langOpen]);

  const isHome = pathname === "/";
  const logoImg = (
    <img
      className="nav--logo-img"
      src={`${withBasePath("/images/logo/Eurofarm_logo.svg")}?v=${logoVersion}`}
      alt="EuroFarm"
    />
  );

  return (
    <header className={`nav${scrolled ? ' is-scrolled' : ''}${phaseCls}`}>
      {isHome ? (
        <span className="nav--logo is-current">{logoImg}</span>
      ) : (
        <Link
          className="nav--logo"
          href="/"
          onClick={(e) => {
            e.preventDefault();
            navigate("/");
          }}
        >
          {logoImg}
        </Link>
      )}

      <nav className="nav--menu" aria-label={t("nav.menu_label")}>
        {navLinks.map((link) => {
          const active =
            link.href === "/"
              ? pathname === "/"
              : pathname.startsWith(link.href);
          return (
            <Link
              key={link.key}
              className={`nav--link${active ? ' is-active' : ''}`}
              href={link.href}
              onClick={(e) => {
                e.preventDefault();
                navigate(link.href);
              }}
            >
              {t(link.key)}
            </Link>
          );
        })}
      </nav>

      <div className="nav--actions">
        {/* Переключатель языка */}
        <div className="nav--lang-wrapper" ref={langRef}>
          <button
            className="nav--lang"
            onClick={() => setLangOpen((v) => !v)}
            aria-label="Выбрать язык"
          >
            {LANGUAGES.find((l) => l.code === lang)?.label ?? lang}
            <Icon name="chevron-down" size={14} />
          </button>

          {langOpen && (
            <div className="nav--lang-drop">
              {LANGUAGES.map((l) => (
                <button
                  key={l.code}
                  className={`nav--lang-option${l.code === lang ? ' is-active' : ''}`}
                  onClick={() => {
                    setLang(l.code);
                    setLangOpen(false);
                  }}
                >
                  {l.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <a className="nav--btn-login" href="#login">{t("nav.login")}</a>
      </div>
    </header>
  );
}
